import React, { Component } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import CustomLoading from '../../../util_components/custom_loading';
import CustomPlayer from '../../../util_components/custom_video_react';
import CustomModal from '../../../util_components/custom_modal';
import ConfirmationPopup from '../../../util_components/confirmation_popup';
import * as actions from '../../../../redux/action_creators';
import { hasAccess } from '../../../../util_functions';
import { trial_video_file_error } from './validate_file';
import './trial_welcome_video.css';

// Roles goserver accepts for upload, replace and delete (PROD-6438).
const MANAGE_ROLES = ['SUPER_USER', 'CARE', 'TEACHER_OPS', 'ENGINEERING'];

class TrialWelcomeVideo extends Component {
  state = {
    confirm_delete: false
  };

  file_input = React.createRef();

  componentDidMount() {
    this.load_video();
  }

  componentDidUpdate(prevProps) {
    const { teacher_uuid } = this.props;
    if (prevProps.teacher_uuid !== teacher_uuid) {
      this.load_video();
    }
  }

  load_video = () => {
    const { teacher_uuid, get_trial_teacher_video } = this.props;
    if (teacher_uuid) {
      get_trial_teacher_video(teacher_uuid);
    }
  };

  open_file_picker = () => this.file_input.current?.click();

  handle_file_change = (e) => {
    const file = e.target.files?.[0];
    // reset so picking the same file again still fires onChange
    e.target.value = '';
    const { teacher_uuid, upload_trial_teacher_video, set_notification_variable } = this.props;
    if (!file) {
      return;
    }
    const error = trial_video_file_error(file);
    if (error) {
      set_notification_variable(true, 'error', error);
      return;
    }
    upload_trial_teacher_video(teacher_uuid, file);
  };

  toggle_confirm_delete = () => {
    const { confirm_delete } = this.state;
    this.setState({ confirm_delete: !confirm_delete });
  };

  delete_video = () => {
    const { teacher_uuid, delete_trial_teacher_video } = this.props;
    this.setState({ confirm_delete: false });
    delete_trial_teacher_video(teacher_uuid);
  };

  // the store holds one teacher's video and upload at a time, so each is shown only on its own teacher's page
  own_video = () => {
    const { teacher_uuid, trial_video, trial_video_teacher_uuid } = this.props;
    return trial_video_teacher_uuid === teacher_uuid ? trial_video : null;
  };

  render_actions = () => {
    const {
      teacher_uuid,
      trial_video_upload_status,
      trial_video_upload_teacher_uuid,
      access_type
    } = this.props;
    if (!hasAccess(access_type, ...MANAGE_ROLES)) {
      return null;
    }
    const trial_video = this.own_video();
    const uploading =
      trial_video_upload_status === 'loading' && trial_video_upload_teacher_uuid === teacher_uuid;
    return (
      <div className="trial-video-actions">
        <button type="button" onClick={this.open_file_picker} disabled={uploading}>
          {trial_video ? 'Replace video' : 'Upload video'}
        </button>
        <input
          ref={this.file_input}
          type="file"
          accept="video/*"
          className="trial-video-file-input"
          onChange={this.handle_file_change}
        />
        {trial_video ? (
          <button type="button" onClick={this.toggle_confirm_delete} disabled={uploading}>
            Delete video
          </button>
        ) : null}
        <span className="trial-video-helper">Upload videos in MP4 format</span>
        {uploading ? <CustomLoading size="small" /> : null}
      </div>
    );
  };

  render_player = () => {
    const trial_video = this.own_video();
    return (
      <div className="trial-video-player">
        {trial_video ? (
          <CustomPlayer
            key={trial_video.uuid}
            fill
            video_url={trial_video.url}
            poster={trial_video.thumbnail_url}
          />
        ) : (
          <div className="trial-video-placeholder">No video uploaded</div>
        )}
      </div>
    );
  };

  render() {
    const { teacher_uuid, trial_video_teacher_uuid, trial_video_status } = this.props;
    const { confirm_delete } = this.state;
    const trial_video = this.own_video();
    const loaded = trial_video_status === 'success' && trial_video_teacher_uuid === teacher_uuid;
    return (
      <div className="teacher-gallery">
        <CustomModal show={confirm_delete} close={this.toggle_confirm_delete}>
          <ConfirmationPopup
            title="Delete this video? Trial students of this teacher will stop getting the video message."
            close_modal={this.toggle_confirm_delete}
            confirm={this.delete_video}
          />
        </CustomModal>
        <div className="teacher-gallery-header">
          Trial Welcome Video:
          {/* the flow is live for this teacher only while a video exists */}
          {loaded ? (
            <span className={`trial-video-badge ${trial_video ? 'trial-video-badge-on' : ''}`}>
              {trial_video ? 'Active' : 'Inactive'}
            </span>
          ) : null}
        </div>
        {this.render_actions()}
        {trial_video_status === 'loading' ? <CustomLoading /> : this.render_player()}
      </div>
    );
  }
}

const video_shape = PropTypes.shape({
  uuid: PropTypes.string,
  url: PropTypes.string,
  thumbnail_url: PropTypes.string
});

TrialWelcomeVideo.propTypes = {
  teacher_uuid: PropTypes.string,
  trial_video: video_shape,
  trial_video_teacher_uuid: PropTypes.string,
  trial_video_status: PropTypes.string,
  trial_video_upload_status: PropTypes.string,
  trial_video_upload_teacher_uuid: PropTypes.string,
  access_type: PropTypes.string,
  get_trial_teacher_video: PropTypes.func.isRequired,
  upload_trial_teacher_video: PropTypes.func.isRequired,
  delete_trial_teacher_video: PropTypes.func.isRequired,
  set_notification_variable: PropTypes.func.isRequired
};

TrialWelcomeVideo.defaultProps = {
  teacher_uuid: '',
  trial_video: null,
  trial_video_teacher_uuid: '',
  trial_video_status: 'none',
  trial_video_upload_status: 'none',
  trial_video_upload_teacher_uuid: '',
  access_type: ''
};

const mapStateToProps = (state) => ({
  trial_video: state.teachers.trial_video,
  trial_video_teacher_uuid: state.teachers.trial_video_teacher_uuid,
  trial_video_status: state.teachers.trial_video_status,
  trial_video_upload_status: state.teachers.trial_video_upload_status,
  trial_video_upload_teacher_uuid: state.teachers.trial_video_upload_teacher_uuid,
  access_type: state.home.access_type
});

const mapDispatchToProps = (dispatch) => ({
  get_trial_teacher_video: (teacher_uuid) => {
    dispatch(actions.get_trial_teacher_video(teacher_uuid));
  },
  upload_trial_teacher_video: (teacher_uuid, file) => {
    dispatch(actions.upload_trial_teacher_video(teacher_uuid, file));
  },
  delete_trial_teacher_video: (teacher_uuid) => {
    dispatch(actions.delete_trial_teacher_video(teacher_uuid));
  },
  set_notification_variable: (show, type, message) => {
    dispatch(actions.set_notification_variable(show, type, message));
  }
});

export default connect(mapStateToProps, mapDispatchToProps)(TrialWelcomeVideo);
